import React, { Component, ReactNode } from 'react';
import { captureException } from 'scripts/logging/error-report';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: string) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to our error reporting service
    captureException(error);

    this.setState({
      error,
      errorInfo: errorInfo.componentStack || null,
    });

    // Also log to console for development
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReportIssue = () => {
    const { error, errorInfo } = this.state;
    const errorDetails = `Error: ${error?.message}\nStack: ${error?.stack}\nComponent Stack: ${errorInfo}`;
    const emailBody = encodeURIComponent(`Hi HAR.com Support Team,

I encountered an error while using HAR AI Mode. Here are the details:

${errorDetails}

Please let me know if you need any additional information.

Best regards`);

    window.open(`mailto:support@har.com?subject=HAR AI Mode Error Report&body=${emailBody}`, '_blank');
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback(this.state.error!, this.state.errorInfo!);
      }

      // Default error UI that matches the project theme
      return (
        <div className="ai_mode">
          <div className="ai_container">
            <div className="container">
              <div className="row" style={{ minHeight: 'calc(100vh - 460px)' }}>
                <div className="col-12 align-self-center text-center h-100">
                  <div className="border-bottom ml-auto mr-auto pb-3 mb-5 pt-5" style={{ maxWidth: '1070px' }}>
                    <img
                      className="img-fluid mb-5 mt-4"
                      src="https://content.harstatic.com/media/icons/a_all/ai_search_dashboard.svg"
                      alt="Error illustration"
                      style={{ maxWidth: '300px', filter: 'grayscale(100%)', opacity: '0.7' }}
                    />
                    <h1 className="mb-5 mt-3 h1--larger" tabIndex={0}>
                      Oops! Something went wrong
                    </h1>
                    <div className="pb-4 mb-2 font_size--large">
                      We encountered an unexpected error while processing your request.
                      Our team has been automatically notified and is working to fix this issue.
                    </div>
                    <div className="ml-auto mr-auto d-inline-block">
                      <div className="row">
                        <div className="col-md-auto col-12 text-center mb-3">
                          <button
                            onClick={this.handleReload}
                            className="btn btn--primary btn--large mr-3"
                          >
                            Try Again
                          </button>
                          <button
                            onClick={this.handleGoHome}
                            className="btn btn--secondary btn--large"
                          >
                            Start Fresh
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-auto mr-auto font_size--large text-left pb-5 mb-4" style={{ maxWidth: '586px' }}>
                    What you can do:
                    <ul className="list--black pt-2">
                      <li>Refresh the page to try again</li>
                      <li>Check your internet connection</li>
                      <li>Clear your browser cache and cookies</li>
                      <li>Try using a different browser</li>
                      <li>
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); this.handleReportIssue(); }}
                          className="text-decoration-none"
                        >
                          Report this issue to our support team
                        </a>
                      </li>
                    </ul>
                  </div>

                  {/* Error details for development/debugging (hidden in production) */}
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <details className="text-left mt-4 mx-auto" style={{ maxWidth: '800px' }}>
                      <summary className="cursor-pointer font_weight--semi_bold mb-2">
                        Error Details (Development Only)
                      </summary>
                      <div className="bg-light p-3 border-radius border">
                        <strong>Error:</strong> {this.state.error.message}
                        <br /><br />
                        <strong>Stack Trace:</strong>
                        <pre className="small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {this.state.error.stack}
                        </pre>
                        {this.state.errorInfo && (
                          <>
                            <br />
                            <strong>Component Stack:</strong>
                            <pre className="small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                              {this.state.errorInfo}
                            </pre>
                          </>
                        )}
                      </div>
                    </details>
                  )}

                  {/* Footer disclaimer matching the app's footer */}
                  <div className="footer_disclaimer font_size--small_extra color_auxiliary text-center pt-4 mt-5">
                    If this problem persists, please contact our{' '}
                    <a
                      className="color_auxiliary"
                      href="https://cms.har.com/help-center/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Help Center
                    </a>
                    {' '}for assistance.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: (error: Error, errorInfo: string) => ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary;
